import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, BookOpen, Upload } from 'lucide-react';
import { areasOfStudyAPI } from '../api';
import type { AreaOfStudy, CreateAreaOfStudyDto, UpdateAreaOfStudyDto } from '../api';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PageHeader } from '../components/common/PageHeader';
import { LoadingState } from '../components/common/LoadingState';
import { ErrorState } from '../components/common/ErrorState';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { useNotification } from '../hooks/useNotification';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

export const AreasOfStudyPage: React.FC = () => {
  const [areasOfStudy, setAreasOfStudy] = useState<AreaOfStudy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [selectedArea, setSelectedArea] = useState<AreaOfStudy | null>(null);
  const [formData, setFormData] = useState<CreateAreaOfStudyDto>({
    name: '',
  });
  const [bulkNames, setBulkNames] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const { showNotification } = useNotification();

  // Fetch areas of study
  const fetchAreasOfStudy = async () => {
    try {
      setLoading(true);
      const data = await areasOfStudyAPI.getAllAreasOfStudy({
        page: currentPage,
        limit: 50,
        search: searchTerm,
        activeOnly: false,
      });

      setAreasOfStudy(data.areas);
      setTotalPages(data.pagination.totalPages);
      setTotal(data.pagination.total);
      setError(null);
    } catch (err) {
      setError('Failed to fetch areas of study');
      console.error('Error fetching areas of study:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAreasOfStudy();
  }, [currentPage, searchTerm]);

  // Handle create
  const handleCreate = async () => {
    try {
      setIsSubmitting(true);
      setFormError(null);

      await areasOfStudyAPI.createAreaOfStudy(formData);

      showNotification('Area of study created successfully', 'success');
      setIsCreateDialogOpen(false);
      resetForm();
      fetchAreasOfStudy();
    } catch (err) {
      const error = err as Error & { response?: { data?: { message?: string } } };
      const errorMessage = error.response?.data?.message ||
                          error.message ||
                          'Failed to create area of study';
      setFormError(errorMessage);
      showNotification(errorMessage, 'error');
      console.error('Error creating area of study:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle edit
  const handleEdit = async () => {
    if (!selectedArea) return;

    try {
      setIsSubmitting(true);
      setFormError(null);

      const updateData: UpdateAreaOfStudyDto = {
        name: formData.name,
      };

      await areasOfStudyAPI.updateAreaOfStudy(selectedArea._id, updateData);

      showNotification('Area of study updated successfully', 'success');
      setIsEditDialogOpen(false);
      resetForm();
      fetchAreasOfStudy();
    } catch (err) {
      const error = err as Error & { response?: { data?: { message?: string } } };
      const errorMessage = error.response?.data?.message ||
                          error.message ||
                          'Failed to update area of study';
      setFormError(errorMessage);
      showNotification(errorMessage, 'error');
      console.error('Error updating area of study:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedArea) return;

    try {
      setIsSubmitting(true);
      setFormError(null);
      await areasOfStudyAPI.deleteAreaOfStudy(selectedArea._id);
      showNotification('Area of study deactivated successfully', 'success');
      setIsDeleteDialogOpen(false);
      setSelectedArea(null);
      fetchAreasOfStudy();
    } catch (err) {
      const error = err as Error & { response?: { data?: { message?: string } } };
      const errorMessage = error.response?.data?.message ||
                          error.message ||
                          'Failed to delete area of study';
      setFormError(errorMessage);
      showNotification(errorMessage, 'error');
      console.error('Error deleting area of study:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle bulk create
  const handleBulkCreate = async () => {
    try {
      setIsSubmitting(true);
      setFormError(null);

      const names = bulkNames
        .split('\n')
        .map(name => name.trim())
        .filter(name => name.length > 0);

      if (names.length === 0) {
        setFormError('Please enter at least one area of study');
        return;
      }

      const result = await areasOfStudyAPI.bulkCreateAreasOfStudy(names);

      const message = `Created: ${result.results.created.length}, Skipped: ${result.results.skipped.length}, Errors: ${result.results.errors.length}`;
      showNotification(message, 'success');
      setIsBulkDialogOpen(false);
      setBulkNames('');
      fetchAreasOfStudy();
    } catch (err) {
      const error = err as Error & { response?: { data?: { message?: string } } };
      const errorMessage = error.response?.data?.message ||
                          error.message ||
                          'Failed to bulk create areas of study';
      setFormError(errorMessage);
      showNotification(errorMessage, 'error');
      console.error('Error bulk creating areas of study:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toggle active status
  const handleToggleActive = async (area: AreaOfStudy) => {
    try {
      await areasOfStudyAPI.updateAreaOfStudy(area._id, {
        isActive: !area.isActive,
      });
      showNotification(`Area of study ${!area.isActive ? 'activated' : 'deactivated'}`, 'success');
      fetchAreasOfStudy();
    } catch (err) {
      const error = err as Error & { response?: { data?: { message?: string } } };
      const errorMessage = error.response?.data?.message ||
                          error.message ||
                          'Failed to update area of study';
      showNotification(errorMessage, 'error');
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
    });
    setSelectedArea(null);
    setFormError(null);
  };

  // Open edit dialog
  const openEditDialog = (area: AreaOfStudy) => {
    setSelectedArea(area);
    setFormData({
      name: area.name,
    });
    setFormError(null);
    setIsEditDialogOpen(true);
  };

  // Open delete dialog
  const openDeleteDialog = (area: AreaOfStudy) => {
    setSelectedArea(area);
    setFormError(null);
    setIsDeleteDialogOpen(true);
  };

  if (loading && currentPage === 1) return <DashboardLayout><LoadingState /></DashboardLayout>;
  if (error && areasOfStudy.length === 0) return <DashboardLayout><ErrorState message={error} onRetry={fetchAreasOfStudy} /></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="responsive-container">
        <div className="space-y-6">
          <PageHeader
            title="Areas of Study Management"
            description="Manage available areas of study for student profiles"
          />

          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
                <div>
                  <CardTitle>All Areas of Study</CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    Total: {total} areas
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
                  <div className="relative flex-1 sm:flex-initial">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      type="text"
                      placeholder="Search areas..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="pl-10 w-full sm:w-64"
                    />
                  </div>
                  <Dialog open={isBulkDialogOpen} onOpenChange={setIsBulkDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" onClick={() => {
                        setBulkNames('');
                        setFormError(null);
                        setIsBulkDialogOpen(true);
                      }}>
                        <Upload className="h-4 w-4 mr-2" />
                        Bulk Add
                      </Button>
                    </DialogTrigger>
                  </Dialog>
                  <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                    <DialogTrigger asChild>
                      <Button onClick={() => {
                        resetForm();
                        setIsCreateDialogOpen(true);
                      }}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Area
                      </Button>
                    </DialogTrigger>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {areasOfStudy.map((area) => (
                      <TableRow key={area._id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <BookOpen className="h-4 w-4 text-gray-400" />
                            {area.name}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleActive(area)}
                          >
                            <Badge variant={area.isActive ? 'default' : 'secondary'}>
                              {area.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                          </Button>
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {new Date(area.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditDialog(area)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openDeleteDialog(area)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {areasOfStudy.length === 0 && (
                <div className="text-center py-12">
                  <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">
                    {searchTerm
                      ? 'No areas of study found matching your search.'
                      : 'No areas of study found. Create one to get started.'}
                  </p>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center space-x-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Create Dialog */}
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Create New Area of Study</DialogTitle>
                <DialogDescription>
                  Add a new area of study for student profiles
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                {formError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                    <p className="text-sm">{formError}</p>
                  </div>
                )}
                <div className="grid gap-2">
                  <Label htmlFor="name">Area of Study Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. Computer Science"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsCreateDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleCreate}
                  disabled={isSubmitting || !formData.name.trim()}
                >
                  {isSubmitting ? 'Creating...' : 'Create'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Edit Dialog */}
          <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Edit Area of Study</DialogTitle>
                <DialogDescription>
                  Update area of study details
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                {formError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                    <p className="text-sm">{formError}</p>
                  </div>
                )}
                <div className="grid gap-2">
                  <Label htmlFor="edit-name">Area of Study Name</Label>
                  <Input
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. Computer Science"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleEdit}
                  disabled={isSubmitting || !formData.name.trim()}
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete Dialog */}
          <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Deactivate Area of Study</DialogTitle>
                <DialogDescription>
                  Are you sure you want to deactivate "{selectedArea?.name}"?
                  It will be hidden from students but can be reactivated later.
                </DialogDescription>
              </DialogHeader>
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                  <p className="text-sm">{formError}</p>
                </div>
              )}
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDeleteDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Deactivating...' : 'Deactivate'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Bulk Create Dialog */}
          <Dialog open={isBulkDialogOpen} onOpenChange={setIsBulkDialogOpen}>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Bulk Add Areas of Study</DialogTitle>
                <DialogDescription>
                  Enter one area of study per line
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                {formError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                    <p className="text-sm">{formError}</p>
                  </div>
                )}
                <div className="grid gap-2">
                  <Label htmlFor="bulk-names">Areas of Study (one per line)</Label>
                  <textarea
                    id="bulk-names"
                    value={bulkNames}
                    onChange={(e) => setBulkNames(e.target.value)}
                    placeholder="Computer Science&#10;Biology&#10;Mathematics&#10;..."
                    className="min-h-[200px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <p className="text-sm text-gray-500">
                    {bulkNames.split('\n').filter(n => n.trim()).length} areas ready to import
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsBulkDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleBulkCreate}
                  disabled={isSubmitting || !bulkNames.trim()}
                >
                  {isSubmitting ? 'Importing...' : 'Import'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </DashboardLayout>
  );
};
